import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiRefreshCw, FiShield } = FiIcons;

const Captcha = ({ onVerify, isVerified }) => {
  const [captchaData, setCaptchaData] = useState({ num1: 0, num2: 0, answer: 0 });
  const [userAnswer, setUserAnswer] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Generate new CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 20) + 1;
    const num2 = Math.floor(Math.random() * 20) + 1;
    const answer = num1 + num2;
    setCaptchaData({ num1, num2, answer });
    setUserAnswer('');
    setIsValid(false);
    setShowError(false);
    setIsProcessing(false);
  };

  // Initialize CAPTCHA on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  // Handle answer submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isProcessing) return;
    
    setIsProcessing(true);
    const userNum = parseInt(userAnswer, 10);
    
    if (userNum === captchaData.answer) {
      setIsValid(true);
      setShowError(false);
      onVerify(true);
    } else {
      setShowError(true);
      setIsValid(false);
      onVerify(false);
      // Clear answer immediately to prevent spam submission
      setUserAnswer('');
      // Generate new CAPTCHA after wrong answer
      setTimeout(() => {
        generateCaptcha();
      }, 1000);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Strict numeric validation to prevent injection
    if (value === '' || /^\d{1,3}$/.test(value)) {
      setUserAnswer(value);
      setShowError(false);
    }
  };

  if (isVerified) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6"
      >
        <div className="flex items-center justify-center gap-2 text-green-600">
          <SafeIcon icon={FiShield} className="text-green-500" />
          <span className="font-semibold">Security Verified</span>
        </div>
        <p className="text-green-700 text-sm text-center mt-1">
          You can now proceed with code entry
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6"
    >
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 text-blue-700 mb-2">
          <SafeIcon icon={FiShield} className="text-blue-500" />
          <span className="font-semibold">Security Verification</span>
        </div>
        <p className="text-blue-600 text-sm">
          Please solve this simple math problem to continue
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* CAPTCHA Display */}
        <div className="bg-white rounded-lg p-4 border-2 border-blue-100 select-none">
          <div className="flex items-center justify-center gap-3 text-2xl font-bold text-gray-700">
            <span className="bg-blue-100 px-3 py-2 rounded-lg">
              {captchaData.num1}
            </span>
            <span className="text-blue-500">+</span>
            <span className="bg-blue-100 px-3 py-2 rounded-lg">
              {captchaData.num2}
            </span>
            <span className="text-blue-500">=</span>
            <span className="text-blue-500">?</span>
          </div>
        </div>

        {/* Answer Input */}
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={userAnswer}
            onChange={handleInputChange}
            placeholder="Your answer"
            disabled={isProcessing}
            className={`flex-1 px-4 py-3 border-2 rounded-xl focus:outline-none transition-all duration-300 text-center font-semibold text-lg ${
              showError
                ? 'border-red-300 focus:border-red-500 bg-red-50'
                : 'border-blue-200 focus:border-blue-500'
            } disabled:opacity-50`}
            maxLength="3"
            required
          />
          
          <motion.button
            type="button"
            onClick={generateCaptcha}
            disabled={isProcessing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-all duration-300 flex items-center justify-center disabled:opacity-50"
            title="Generate new CAPTCHA"
          >
            <SafeIcon icon={FiRefreshCw} className={`text-lg ${isProcessing ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={!userAnswer.trim() || isProcessing}
          whileHover={{ scale: userAnswer.trim() && !isProcessing ? 1.02 : 1 }}
          whileTap={{ scale: userAnswer.trim() && !isProcessing ? 0.98 : 1 }}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiShield} />
          {isProcessing ? 'Verifying...' : 'Verify & Continue'}
        </motion.button>

        {/* Error Message */}
        {showError && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-lg p-3 text-center"
          >
            <p className="text-red-600 text-sm font-medium">
              Incorrect answer. Please try again.
            </p>
          </motion.div>
        )}
      </form>
    </motion.div>
  );
};

export default Captcha;